import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Filter, Check, X, FileText, AlertTriangle, Loader2, RotateCcw, Pencil, Trash2, Download, Ban } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import api from "../../utils/api";
import { toast } from "sonner";
import { toSydneyDate, formatSydneyTime } from "../../utils/helpers";
import { cn } from "../../components/ui/utils";
import { LeaveHandoverSummary } from "../../components/LeaveHandoverSummary";

type TabType = "Pending" | "Approved" | "Rejected";

interface LeaveBalance {
  quota: number;
  used: number;
  adjustment: number;
  remaining: number;
}

interface LeaveBalances {
  [key: string]: LeaveBalance;
}

const getTypeBadgeColor = (type: string) => {
  switch (type) {
    case "Annual Leave":
      return "bg-green-100 text-green-700";
    case "Personal Leave":
      return "bg-blue-100 text-blue-700";
    case "Unpaid Leave":
      return "bg-orange-100 text-orange-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case "Approved":
      return "bg-gray-100 text-gray-700"; // Based on screenshot it is greyish with black text
    case "Pending":
      return "bg-yellow-100 text-yellow-800";
    case "Cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

// Shared cancel confirmation for an approved request (team and self-service views).
const CancelLeaveButton = ({ processing, onConfirm }: { processing: boolean; onConfirm: () => void }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button
        variant="outline"
        disabled={processing}
        className="flex-1 rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all border-red-100 text-red-600 hover:bg-red-50 hover:border-red-200 bg-white disabled:opacity-50"
      >
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
        Cancel Leave
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel this approved leave?</AlertDialogTitle>
        <AlertDialogDescription>
          The leave will be closed and the balance restored. Unpaid leave will also accrue back for those days.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Keep Leave</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-[#da2143] hover:bg-[#c01e38]">
          Cancel Leave
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const ApproveRequests = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>("Pending");
  const [requestScope, setRequestScope] = useState<'team' | 'my'>("team");
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [leaveBalances, setLeaveBalances] = useState<Record<string, LeaveBalances>>({});
  const [balancesLoading, setBalancesLoading] = useState<Record<string, boolean>>({});
  const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'my-requests') {
      setRequestScope('my');
    }
  }, [searchParams]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const endpoint = requestScope === 'team' ? '/api/attendance/leave-approvals' : '/api/attendance/my-leave-requests';
      const res = await api.get(`${endpoint}?status=${activeTab}`);
      if (res.success) {
        setRequests(res.data);
        // Fetch balances for each request's employee (only for team scope + Pending tab)
        if (requestScope === 'team' && activeTab === 'Pending') {
          res.data.forEach((req: any) => {
            if (req.employee?.id && req.type !== 'Unpaid Leave') {
              fetchEmployeeBalance(req.employee.id, req.type);
            }
          });
        }
      } else {
        toast.error("Failed to fetch leave requests");
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("An error occurred while fetching requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeBalance = async (employeeId: string, leaveType: string) => {
    // Skip if already loading or loaded for this employee
    if (balancesLoading[employeeId] || leaveBalances[employeeId]) return;

    setBalancesLoading(prev => ({ ...prev, [employeeId]: true }));
    try {
      const res = await api.get(`/api/attendance/leave-balance?employeeId=${employeeId}`);
      if (res.success && res.data) {
        setLeaveBalances(prev => ({ ...prev, [employeeId]: res.data }));
      }
    } catch (error) {
      console.error("Error fetching leave balance for employee", employeeId, error);
    } finally {
      setBalancesLoading(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [activeTab, requestScope]);

  const handleAction = async (id: string, status: "Approved" | "Rejected" | "Pending") => {
    try {
      setProcessingId(id);
      const res = await api.patch(`/api/attendance/leave-approvals/${id}`, { status });
      if (res.success) {
        toast.success(`Request ${status.toLowerCase()} successfully`);
        // Refresh list
        fetchRequests();
      } else {
        toast.error(res.message || `Failed to ${status.toLowerCase()} request`);
      }
    } catch (error) {
      console.error(`Error ${status.toLowerCase()} request:`, error);
      toast.error(`An error occurred while ${status.toLowerCase()} the request`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      setProcessingId(id);
      const res = await api.post(`/api/attendance/leave-request/${id}/cancel`, {});
      if (res.success) {
        toast.success("Leave request cancelled");
        fetchRequests();
      } else {
        toast.error(res.message || "Failed to cancel the request");
      }
    } catch (error) {
      console.error("Error cancelling request:", error);
      toast.error("An error occurred while cancelling the request");
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveClick = (id: string) => {
    setPendingApprovalId(id);
  };
  const handleConfirmApprove = async () => {
    if (!pendingApprovalId) return;
    await handleAction(pendingApprovalId, 'Approved');
    setPendingApprovalId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      setProcessingId(id);
      const res = await api.delete(`/api/attendance/leave-request/${id}`);
      if (res.success) {
        toast.success("Request deleted successfully");
        fetchRequests();
      } else {
        toast.error(res.message || "Failed to delete request");
      }
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error("An error occurred while deleting the request");
    } finally {
      setProcessingId(null);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleDownloadAttachment = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      window.open(url, '_blank');
    }
  };

  const parseDurationHours = (durationStr: string): number => {
    const match = durationStr.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : 0;
  };

  const formatOverlapDates = (datesStr: string): string => {
    const parts = datesStr.split(' to ');
    return parts.map(part => toSydneyDate(part.trim())).join(' to ');
  };

  const renderBalanceCard = (req: any) => {
    const employeeId = req.employee?.id;
    const balances = employeeId ? leaveBalances[employeeId] : null;
    const isLoading = employeeId ? balancesLoading[employeeId] : false;
    const requestHours = parseDurationHours(req.duration);

    // Don't show for Unpaid Leave or if no balance data available yet
    if (req.type === 'Unpaid Leave') return null;
    if (!balances || !balances[req.type]) return null;

    const balance = balances[req.type];
    const currentRemaining = balance.remaining;
    const projectedRemaining = currentRemaining - requestHours;

    let colorClass = "bg-green-500";
    let bgColorClass = "bg-green-100";
    if (req.type === "Personal Leave") {
      colorClass = "bg-blue-500";
      bgColorClass = "bg-blue-100";
    } else if (req.type === "Annual Leave") {
      colorClass = "bg-green-500";
      bgColorClass = "bg-green-100";
    }

    const projectedUsed = balance.used + requestHours;

    return (
      <Card className="rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
        <CardHeader className="bg-gray-50/50 border-b border-gray-50 pb-4">
          <CardTitle className="text-sm font-bold text-gray-900">
            {req.employee.name}'s {req.type} Balance
          </CardTitle>
          <CardDescription className="text-xs">Current available hours</CardDescription>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          {/* Current Balance */}
          <div>
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-gray-500 font-medium">Current Balance</span>
              <span className="font-bold text-gray-900">{currentRemaining.toFixed(4)} hours</span>
            </div>
          </div>

          {/* Request Hours */}
          <div>
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-gray-500 font-medium">This Request</span>
              <span className="font-bold text-amber-600">- {requestHours.toFixed(2)} hours</span>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Projected Balance */}
          <div>
            <div className="flex justify-between items-center text-sm mb-2">
              <span className="text-gray-900 font-bold">Balance if Approved</span>
              <span className={cn("font-bold", projectedRemaining < 0 ? "text-red-600" : "text-gray-900")}>
                {projectedRemaining.toFixed(4)} hours
              </span>
            </div>
            <Progress
              value={Math.min((projectedUsed / (balance.quota + balance.adjustment || 1)) * 100, 100)}
              className={cn("h-2", bgColorClass)}
              indicatorClassName={cn(colorClass, "opacity-80")}
            />
            {projectedRemaining < 0 && (
              <p className="text-[10px] text-red-600 font-medium mt-1.5">
                ⚠️ This will exceed the available balance
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
            onClick={() => navigate("/attendance")}
          >
            <ChevronLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              {requestScope === 'team' ? 'Team Leave Requests' : 'My Leave Requests'}
            </h1>
            <p className="text-gray-500 font-medium text-sm">
              {requestScope === 'team'
                ? 'Review and approve team leave requests'
                : 'Track the status of your own leave requests'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Scope Toggle and Tabs Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Scope Toggle */}
          <div className="inline-flex bg-gray-100/80 p-1 rounded-xl">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${requestScope === "my" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              onClick={() => setRequestScope("my")}
            >
              My Requests
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${requestScope === "team" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              onClick={() => setRequestScope("team")}
            >
              Team Requests
            </button>
          </div>

          {/* Tabs */}
          <div className="inline-flex bg-gray-100/80 p-1 rounded-xl">
            {(["Pending", "Approved", "Rejected"] as TabType[]).map((tab) => (
              <button
                key={tab}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === tab ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Request Cards */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 italic text-gray-400">
              <Loader2 className="h-8 w-8 animate-spin mb-2" />
              <p>Loading requests...</p>
            </div>
          ) : requests.map((req) => (
            <div key={req.id} className="bg-white rounded-[24px] border border-gray-100 shadow-sm p-6 space-y-6">

              {/* Card Header */}
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12 text-sm font-bold border border-gray-100 shadow-sm">
                    <AvatarFallback className="bg-gray-50 text-gray-700">
                      {getInitials(req.employee.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{req.employee.name}</h3>
                    <p className="text-sm text-gray-500 font-medium">{req.employee.department?.departmentName || 'No Department'}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge className={`shadow-none px-3 py-1 rounded-full text-xs font-bold border-0 ${getTypeBadgeColor(req.type)} hover:${getTypeBadgeColor(req.type)}`}>
                    {req.type}
                  </Badge>
                  {requestScope === 'my' && (
                    <Badge className={`shadow-none px-3 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border-0 ${getStatusBadgeColor(req.status)}`}>
                      {req.status}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Data Grid + Balance Card Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left column: Date info */}
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-1">Start Date</p>
                    <p className="text-sm font-bold text-gray-900">{toSydneyDate(req.startDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-1">End Date</p>
                    <p className="text-sm font-bold text-gray-900">{toSydneyDate(req.endDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-1">Duration</p>
                    <p className="text-sm font-bold text-gray-900">{req.duration}</p>
                  </div>
                </div>

                {/* Right column: Balance Card (only for team scope + Pending tab + non-unpaid leave) */}
                {requestScope === 'team' && activeTab === 'Pending' && req.type !== 'Unpaid Leave' && (
                  <div className="lg:col-span-1">
                    {balancesLoading[req.employee?.id] ? (
                      <Card className="rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                        <CardContent className="p-6 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </CardContent>
                      </Card>
                    ) : (
                      renderBalanceCard(req)
                    )}
                  </div>
                )}
              </div>

              {/* Reason */}
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Reason</p>
                <p className="text-sm font-medium text-gray-900">{req.reason}</p>
              </div>

              {/* Handover confirmation (Annual Leave only) */}
              <LeaveHandoverSummary handover={req.handover} />

              {/* Approval/Rejection Details */}
              {(activeTab === "Approved" || activeTab === "Rejected") && req.approvedBy && (
                <div className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0">
                    {activeTab === "Approved" ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-600" />}
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-900 font-bold leading-tight">
                      {activeTab === "Approved" ? "Approved" : "Rejected"} by {req.approvedBy.name}
                    </p>
                    <p className="text-gray-500 font-medium text-xs mt-0.5">
                      on {toSydneyDate(req.updatedAt)} at {formatSydneyTime(req.updatedAt)}
                    </p>
                  </div>
                </div>
              )}

              {/* Attachments */}
              {req.attachments && req.attachments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 font-medium mb-1">Attachments</p>
                  <div className="flex flex-wrap gap-3">
                    {req.attachments.map((file: any, idx: number) => (
                      <div
                        key={idx}
                        onClick={() => requestScope === 'team' ? handleDownloadAttachment(file.url, file.filename) : window.open(file.url, '_blank')}
                        className="flex items-center gap-2 bg-gray-50 border border-gray-100 px-3 py-2 rounded-xl group transition-all hover:bg-white hover:border-gray-200 cursor-pointer"
                      >
                        {requestScope === 'team' ? (
                          <Download className="h-4 w-4 text-blue-500" />
                        ) : (
                          <FileText className="h-4 w-4 text-blue-500" />
                        )}
                        <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 truncate max-w-[150px]">
                          {file.filename || `Attachment ${idx + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overlapping Warning */}
              {requestScope === 'team' && req.overlapping.length > 0 && (
                <div className="bg-orange-50/50 border border-orange-100 rounded-2xl p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                    <p className="text-sm font-bold text-orange-900">Multiple team members on leave during this period:</p>
                  </div>

                  <div className="space-y-2 mb-4 pl-7">
                    {req.overlapping.map((overlap: any, idx: number) => (
                      <div key={idx} className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-gray-900 font-medium">{overlap.name} <span className="text-gray-500 font-normal">({overlap.department})</span></span>
                        <span className="text-gray-500">-</span>
                        <span className="text-gray-600 font-medium">{formatOverlapDates(overlap.dates)}</span>
                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${getStatusBadgeColor(overlap.status)}`}>
                          {overlap.status}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm text-orange-800 font-medium pl-7">Consider team coverage before approving this request.</p>
                </div>
              )}

              {/* Action Buttons */}
              {requestScope === 'team' && activeTab === "Pending" && (
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Button
                    onClick={() => handleApproveClick(req.id)}
                    disabled={processingId === req.id}
                    className="flex-1 bg-black text-white hover:bg-gray-800 rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all disabled:opacity-50"
                  >
                    {processingId === req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleAction(req.id, 'Rejected')}
                    disabled={processingId === req.id}
                    className="flex-1 bg-[#da2143] text-white hover:bg-[#c01e38] rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all disabled:opacity-50"
                  >
                    {processingId === req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                    Reject
                  </Button>
                  <Button
                    onClick={() => navigate(`/attendance/request-leave/${req.id}`)}
                    className="flex-1 bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit Request
                  </Button>
                </div>
              )}

              {/* Cancel / Revoke for Approved or Rejected */}
              {requestScope === 'team' && (activeTab === "Approved" || activeTab === "Rejected") && (
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  {activeTab === "Approved" && (
                    <CancelLeaveButton
                      processing={processingId === req.id}
                      onConfirm={() => handleCancel(req.id)}
                    />
                  )}
                  <Button
                    onClick={() => handleAction(req.id, 'Pending')}
                    disabled={processingId === req.id}
                    variant="outline"
                    className="flex-1 rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {processingId === req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    Revoke & Return to Pending
                  </Button>
                </div>
              )}

              {/* Actions for My Pending Requests */}
              {requestScope === 'my' && req.status === "Pending" && (
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Button
                    onClick={() => navigate(`/attendance/request-leave/${req.id}`)}
                    variant="outline"
                    className="flex-1 rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all border-gray-200 text-gray-600 hover:bg-gray-50 bg-white"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit Request
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={processingId === req.id}
                        className="flex-1 rounded-xl h-12 flex items-center justify-center gap-2 font-bold transition-all border-red-100 text-red-600 hover:bg-red-50 hover:border-red-200 bg-white disabled:opacity-50"
                      >
                        {processingId === req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        Delete Request
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-[32px] border-gray-100 p-8">
                      <AlertDialogHeader className="space-y-3">
                        <AlertDialogTitle className="text-2xl font-bold text-gray-900">Are you sure you want to delete this request?</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-500 font-medium">
                          This will permanently delete your leave request. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="mt-8 gap-3 sm:justify-end">
                        <AlertDialogCancel className="rounded-xl font-bold border-gray-100 hover:bg-gray-50 px-6 h-12">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(req.id)}
                          className="rounded-xl font-bold bg-red-600 text-white hover:bg-red-700 px-6 h-12"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              {/* Cancel for My Approved Requests */}
              {requestScope === 'my' && req.status === "Approved" && (
                <div className="flex pt-2">
                  <CancelLeaveButton
                    processing={processingId === req.id}
                    onConfirm={() => handleCancel(req.id)}
                  />
                </div>
              )}

            </div>
          ))}

          {requests.length === 0 && !loading && (
            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm p-12 text-center">
              <p className="text-gray-500 font-medium">No requests found in this category.</p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!pendingApprovalId} onOpenChange={(open) => !open && setPendingApprovalId(null)}>
        <AlertDialogContent className="rounded-[32px] border-gray-100 p-8">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-2xl font-bold text-gray-900">Confirm Approval</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 font-medium">
              Please ensure your employees has selected the correct leave type by reviewing the leave definitions and reasons for leave before approving. If incorrect, please reject and have the request re-submitted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-8 gap-3 sm:justify-end">
            <AlertDialogCancel onClick={() => setPendingApprovalId(null)} className="rounded-xl font-bold border-gray-100 hover:bg-gray-50 px-6 h-12">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmApprove} className="rounded-xl font-bold bg-black text-white hover:bg-gray-800 px-6 h-12">Approve</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ApproveRequests;
